import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Checkbox, IconButton, InputBase, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Loading from 'components/common/Loading';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import getFile from 'utils/getFile';
import { I18n } from 'utils/i18n';
import { formatOmmnioSignDate, normalizeOmmnioSignDate } from '../date-utils';
import SignatureDialog from './SignatureDialog';

const Document = React.lazy(() => import('components/common/PDFPreviewLazy/Document'));
const Page = React.lazy(() => import('components/common/PDFPreviewLazy/Page'));

const PDF_SCALE_CLASS = 'ommniosign-pdf-scale-target';

const FieldOverlay = styled('div', {
  shouldForwardProp: prop => !['isActive', 'isFilled', 'isMandatory', 'isSignature'].includes(prop),
})(({ theme, isActive, isFilled, isMandatory, isSignature }) => ({
  position: 'absolute',
  padding: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  backgroundColor: 'rgba(104,104,104,0.1)',
  border: '2px solid #a4a4a4',
  color: '#a4a4a4',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
  ...(isSignature && {
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: 0,
    justifyContent: 'flex-start',
  }),
  '&:hover': {
    backgroundColor: 'rgba(104,104,104,0.25)',
    transform: 'scale(1.02)',
  },
  ...(isMandatory &&
    !isActive &&
    !isFilled && {
      border: '2px solid #5568E8',
      color: '#5568E8',
      backgroundColor: 'rgba(85, 104, 232, 0.15)',
      '&:hover': {
        backgroundColor: 'rgba(85, 104, 232, 0.25)',
        transform: 'scale(1.02)',
      },
    }),
  ...(isFilled &&
    !isActive && {
      backgroundColor: 'rgba(40, 167, 69, 0.1)',
      color: '#000',
      cursor: 'default',
      border: '2px solid #28a745',
      '&:hover': {
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        transform: 'none',
      },
    }),
  ...(isActive && {
    border: `3px solid ${theme.palette.info.main}`,
    backgroundColor: 'rgba(0, 110, 255, 0.1)',
    '@keyframes ommniosignFieldPulse': {
      '0%, 100%': {
        boxShadow: theme.shadows[3],
      },
      '50%': {
        boxShadow: `0 0 20px ${theme.palette.info.main}`,
      },
    },
    animation: 'ommniosignFieldPulse 2s infinite',
    '&:hover': {
      backgroundColor: 'rgba(0, 102, 255, 0.1)',
      transform: 'none',
    },
  }),
}));

const ScrollIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(1.5, 2),
  borderRadius: '50px',
  boxShadow: theme.shadows[8],
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '@keyframes ommniosignFadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateX(-50%) translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateX(-50%) translateY(0)',
    },
  },
  animation: 'ommniosignFadeInUp 0.3s ease-out',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const ScrollIndicatorIcon = styled(ArrowUpwardIcon)({
  fontSize: '1.25rem',
  '@keyframes ommniosignBounce': {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '50%': {
      transform: 'translateY(-4px)',
    },
  },
  animation: 'ommniosignBounce 1s infinite',
});

const rootSx = {
  position: 'relative',
  width: '100%',
  height: '100%',
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

const rootCompactSx = {
  height: '100%',
  flex: 1,
  minHeight: 0,
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const previewWrapperSx = theme => ({
  flex: 1,
  minHeight: 0,
  backgroundColor: '#f2f4f7',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  position: 'relative',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 1,
  WebkitOverflowScrolling: 'touch',
});

const previewWrapperCompactSx = theme => ({
  flex: 1,
  minHeight: 0,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  position: 'relative',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 1,
  WebkitOverflowScrolling: 'touch',
});

const scrollSx = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  position: 'relative',
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
};

const pdfSx = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'visible',
  alignItems: 'flex-start',
  minWidth: 'fit-content',
};

const pageSx = {
  width: 'fit-content',
  margin: '20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: '#fff',
  boxShadow:
    '0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 5px 8px 0px rgb(0 0 0 / 14%), 0px 1px 14px 0px rgb(0 0 0 / 12%)',
  overflow: 'hidden',
  position: 'relative',
};

const fieldsContainerSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 1,
  pointerEvents: 'none',
  '& > *': {
    pointerEvents: 'auto',
  },
};

const fieldLabelSx = theme => ({
  fontSize: '0.9rem',
  color: 'inherit',
  overflow: 'visible',
  lineHeight: 0.8,
  [theme.breakpoints.up('sm')]: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginRight: '-20px',
  },
});

const textInputSx = {
  backgroundColor: 'transparent',
  '& input': {
    color: '#333',
    fontSize: '0.9rem',
  },
};

const checkIconSx = theme => ({
  marginLeft: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    position: 'absolute',
    right: '-20px',
  },
});

const zoomControlsSx = theme => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: 1,
  boxShadow: theme.shadows[4],
  overflow: 'hidden',
});

const zoomButtonSx = theme => ({
  minWidth: 40,
  minHeight: 40,
  padding: theme.spacing(1),
  borderRadius: 0,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:disabled': {
    color: theme.palette.action.disabled,
  },
});

const zoomLabelSx = theme => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  textAlign: 'center',
  padding: theme.spacing(0.5, 1),
  color: theme.palette.text.secondary,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  minWidth: 40,
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:active': {
    backgroundColor: theme.palette.action.selected,
  },
});

const PDFViewerWithOverlays = ({
  pdfData,
  fields = [],
  widgets,
  onWidgetChange,
  onAllFieldsFilled = null,
  onScrollToBottom = null,
  currentSignerIndex = null,
  compact = false,
}) => {
  const containerRef = useRef(null);
  const [data, setData] = useState(null);
  const [dimensions, setDimensions] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const currentSignatureFieldIdRef = useRef(null);
  const currentSignatureFieldRef = useRef(null);
  const hasScrolledToBottomRef = useRef(false);
  const fieldRefs = useRef({});
  const [activeFieldDirection, setActiveFieldDirection] = useState(null); // 'above' | 'below' | null
  const [zoom, setZoom] = useState(1); // Zoom level: 1 = 100%, 1.5 = 150%, etc.

  // Zoom constants
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.25;

  const handleZoomIn = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
      return;
    }

    // Calculate center point relative to content
    const scrollCenterX = container.scrollLeft + container.clientWidth / 2;
    const scrollCenterY = container.scrollTop + container.clientHeight / 2;

    setZoom(prev => {
      const newZoom = Math.min(prev + ZOOM_STEP, MAX_ZOOM);
      const scale = newZoom / prev;

      // Adjust scroll to keep the same center point
      requestAnimationFrame(() => {
        container.scrollLeft = scrollCenterX * scale - container.clientWidth / 2;
        container.scrollTop = scrollCenterY * scale - container.clientHeight / 2;
      });

      return newZoom;
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      setZoom(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
      return;
    }

    // Calculate center point relative to content
    const scrollCenterX = container.scrollLeft + container.clientWidth / 2;
    const scrollCenterY = container.scrollTop + container.clientHeight / 2;

    setZoom(prev => {
      const newZoom = Math.max(prev - ZOOM_STEP, MIN_ZOOM);
      const scale = newZoom / prev;

      // Adjust scroll to keep the same center point
      requestAnimationFrame(() => {
        container.scrollLeft = scrollCenterX * scale - container.clientWidth / 2;
        container.scrollTop = scrollCenterY * scale - container.clientHeight / 2;
      });

      return newZoom;
    });
  }, []);

  // Pinch-to-zoom support using CSS transform for smooth visual updates
  // We apply a CSS transform during pinching (no React re-renders),
  // then commit the final zoom value to state when pinch ends
  const isPinchingRef = useRef(false);
  const startZoomRef = useRef(zoom);
  const startDistanceRef = useRef(null);

  useEffect(() => {
    const getDistance = (touch1, touch2) => {
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = e => {
      if (e.touches.length === 2) {
        e.preventDefault();
        isPinchingRef.current = true;
        startDistanceRef.current = getDistance(e.touches[0], e.touches[1]);
        startZoomRef.current = zoom;
      }
    };

    const handleTouchMove = e => {
      if (e.touches.length === 2 && isPinchingRef.current && startDistanceRef.current !== null) {
        e.preventDefault();

        const currentDistance = getDistance(e.touches[0], e.touches[1]);
        const ratio = currentDistance / startDistanceRef.current;

        // Calculate the visual scale relative to current zoom
        // Clamp the effective zoom within bounds
        const effectiveZoom = Math.min(Math.max(startZoomRef.current * ratio, MIN_ZOOM), MAX_ZOOM);
        const visualScale = effectiveZoom / startZoomRef.current;

        // Apply CSS transform for instant visual feedback (no React re-render)
        const pdfElement = containerRef.current?.querySelector(`.${PDF_SCALE_CLASS}`);
        if (pdfElement) {
          pdfElement.style.transformOrigin = 'top left';
          pdfElement.style.transform = `scale(${visualScale})`;
        }
      }
    };

    const handleTouchEnd = () => {
      if (isPinchingRef.current && startDistanceRef.current !== null) {
        // Calculate final zoom from the last known state
        const pdfElement = containerRef.current?.querySelector(`.${PDF_SCALE_CLASS}`);

        if (pdfElement) {
          // Get the current transform scale
          const transform = pdfElement.style.transform;
          const match = transform.match(/scale\(([\d.]+)\)/);
          const visualScale = match ? parseFloat(match[1]) : 1;

          // Calculate final zoom
          const finalZoom = Math.min(
            Math.max(startZoomRef.current * visualScale, MIN_ZOOM),
            MAX_ZOOM,
          );

          // Remove the CSS transform
          pdfElement.style.transform = '';
          pdfElement.style.transformOrigin = '';

          // Update React state with the final zoom value
          setZoom(finalZoom);
        }

        isPinchingRef.current = false;
        startDistanceRef.current = null;
      }
    };

    const scrollContainer = containerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('touchstart', handleTouchStart, { passive: false });
      scrollContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
      scrollContainer.addEventListener('touchend', handleTouchEnd);
      scrollContainer.addEventListener('touchcancel', handleTouchEnd);

      return () => {
        scrollContainer.removeEventListener('touchstart', handleTouchStart);
        scrollContainer.removeEventListener('touchmove', handleTouchMove);
        scrollContainer.removeEventListener('touchend', handleTouchEnd);
        scrollContainer.removeEventListener('touchcancel', handleTouchEnd);
      };
    }
    return undefined;
  }, [zoom]);

  // Filter fields for current signer (if currentSignerIndex is provided)
  const filteredFields = React.useMemo(() => {
    if (!fields) return [];
    // If currentSignerIndex is null/undefined, show all fields (onePerMember mode)
    if (currentSignerIndex === null || currentSignerIndex === undefined) return fields;
    // In oneForAll mode, only show fields for current signer
    return fields.filter(field => field.signerIndex === currentSignerIndex);
  }, [fields, currentSignerIndex]);

  // Helper function to get field ID consistently
  const getFieldId = field => field._id || field.id || field.name;

  // Helper function to get prefill value for a field.
  // The signing-page endpoint (GET /v1/ommniosign/sign/:requestId/:signerToken) already resolves
  // dynamic prefill references server-side via `resolvePrefillValue(field.prefillValue, employee,
  // company)` before responding, so the client only ever sees static values here. We must NOT
  // re-resolve from `state.auth.employee` / `state.auth.company_id` because the OmmnioSign signing
  // route isn't auth-gated and those selectors are empty on a fresh WebView (e.g. after RN app
  // reinstall) — which caused signer/company prefill widgets to render blank.
  const getPrefillValue = useCallback(field => {
    if (field.prefillValue === undefined || field.prefillValue === null) return null;
    if (
      typeof field.prefillValue === 'string' ||
      typeof field.prefillValue === 'boolean' ||
      typeof field.prefillValue === 'number'
    ) {
      return field.prefillValue;
    }
    return null;
  }, []);

  useEffect(() => {
    if (!data) {
      if (pdfData instanceof Blob || pdfData instanceof File) {
        setData(pdfData);
      } else if (typeof pdfData === 'string') {
        getFile(pdfData)
          .then(d => setData(d))
          .catch(e => {
            console.error('Error loading PDF:', e);
            setLoaded(true);
          });
      }
    }
  }, [data, pdfData]);

  // Initialize prefilled values and first unfilled field on mount
  useEffect(() => {
    if (filteredFields && filteredFields.length > 0) {
      // Initialize prefilled values
      const prefillWidgets = {};
      filteredFields.forEach(field => {
        const fieldId = getFieldId(field);
        const prefillValue = getPrefillValue(field);
        if (prefillValue !== null && !widgets[fieldId]) {
          prefillWidgets[fieldId] =
            field.type === 'date' ? normalizeOmmnioSignDate(prefillValue) : prefillValue;
        }
      });

      // Apply prefill values
      if (Object.keys(prefillWidgets).length > 0) {
        Object.entries(prefillWidgets).forEach(([fieldId, value]) => {
          onWidgetChange(fieldId, value);
        });
      }

      // Set first unfilled field as active
      if (activeFieldId === null) {
        const nextUnfilled = filteredFields.find(
          field => !widgets[getFieldId(field)] && !prefillWidgets[getFieldId(field)],
        );
        if (nextUnfilled) {
          setActiveFieldId(getFieldId(nextUnfilled));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredFields]); // Only run when filtered fields change

  // Check if all mandatory/required fields are filled
  useEffect(() => {
    if (filteredFields && filteredFields.length > 0 && onAllFieldsFilled) {
      // Only check mandatory/required fields for current signer
      const mandatoryFields = filteredFields.filter(field => field.mandatory || field.required);

      // If there are no mandatory fields, consider all filled
      if (mandatoryFields.length === 0) {
        onAllFieldsFilled(true);
        return;
      }

      // Check if all mandatory fields are filled
      const allMandatoryFilled = mandatoryFields.every(field => widgets[getFieldId(field)]);
      onAllFieldsFilled(allMandatoryFilled);
    }
  }, [filteredFields, widgets, onAllFieldsFilled]);

  // Detect scroll to bottom (eIDAS compliance tracking)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onScrollToBottom) return () => {};

    const handleScroll = () => {
      if (hasScrolledToBottomRef.current) return; // Already tracked

      const { scrollTop, scrollHeight, clientHeight } = container;
      // Check if scrolled to bottom (within 50px threshold)
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;

      if (isAtBottom) {
        hasScrolledToBottomRef.current = true;
        onScrollToBottom();
      }
    };

    container.addEventListener('scroll', handleScroll);
    // Also check immediately in case content is already visible without scrolling
    handleScroll();

    return () => container.removeEventListener('scroll', handleScroll);
  }, [onScrollToBottom]);

  const handleFieldClick = field => {
    const fieldId = getFieldId(field);

    if (!fieldId) {
      console.error('PDFViewerWithOverlays: Field has no id property!', field);
      return;
    }

    setActiveFieldId(fieldId);

    if (field.type === 'signature') {
      currentSignatureFieldIdRef.current = fieldId;
      currentSignatureFieldRef.current = field;
      setSignatureDialogOpen(true);
    }
  };

  const handleSignatureSave = useCallback(
    (fieldId, signatureData) => {
      onWidgetChange(fieldId, signatureData);
      setSignatureDialogOpen(false);
      // Move to next unfilled field
      const currentIndex = filteredFields.findIndex(f => getFieldId(f) === fieldId);
      const nextUnfilled = filteredFields
        .slice(currentIndex + 1)
        .find(field => !widgets[getFieldId(field)]);
      if (nextUnfilled) {
        setActiveFieldId(getFieldId(nextUnfilled));
      } else {
        setActiveFieldId(null);
      }
    },
    [onWidgetChange, filteredFields, widgets],
  );

  const handleFieldBlur = useCallback(
    fieldId => {
      // When a text/date field loses focus, move to next unfilled field
      const currentIndex = filteredFields.findIndex(f => getFieldId(f) === fieldId);
      const nextUnfilled = filteredFields
        .slice(currentIndex + 1)
        .find(field => !widgets[getFieldId(field)]);
      if (nextUnfilled) {
        setActiveFieldId(getFieldId(nextUnfilled));
      } else {
        setActiveFieldId(null);
      }
    },
    [filteredFields, widgets],
  );

  const handleScrollToField = fieldId => {
    const fieldRef = fieldRefs.current[fieldId];
    if (fieldRef && containerRef.current) {
      const fieldRect = fieldRef.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const scrollTop = containerRef.current.scrollTop;
      const scrollLeft = containerRef.current.scrollLeft;

      // Calculate the position to scroll to (center the field)
      const targetScrollTop =
        scrollTop +
        fieldRect.top -
        containerRect.top -
        containerRect.height / 2 +
        fieldRect.height / 2;

      const targetScrollLeft =
        scrollLeft +
        fieldRect.left -
        containerRect.left -
        containerRect.width / 2 +
        fieldRect.width / 2;

      containerRef.current.scrollTo({
        top: targetScrollTop,
        left: targetScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  // Check if active field is off-screen and calculate angle to it
  const checkFieldVisibility = useCallback(() => {
    if (!activeFieldId || !containerRef.current) {
      setActiveFieldDirection(null);
      return;
    }

    const fieldElement = fieldRefs.current[activeFieldId];
    if (!fieldElement) {
      // Field not rendered yet
      setActiveFieldDirection(null);
      return;
    }

    const fieldRect = fieldElement.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    // Check if field is partially or fully outside the visible container area
    const isAbove = fieldRect.bottom < containerRect.top + 50;
    const isBelow = fieldRect.top > containerRect.bottom - 50;
    const isLeft = fieldRect.right < containerRect.left + 50;
    const isRight = fieldRect.left > containerRect.right - 50;

    if (isAbove || isBelow || isLeft || isRight) {
      // Calculate center points
      const fieldCenterX = fieldRect.left + fieldRect.width / 2;
      const fieldCenterY = fieldRect.top + fieldRect.height / 2;
      const containerCenterX = containerRect.left + containerRect.width / 2;
      const containerCenterY = containerRect.top + containerRect.height / 2;

      // Calculate angle from container center to field center
      // atan2 returns radians, convert to degrees
      // Subtract 90 because ArrowUpwardIcon points up (negative Y) by default
      const angleRad = Math.atan2(fieldCenterY - containerCenterY, fieldCenterX - containerCenterX);
      const angleDeg = (angleRad * 180) / Math.PI + 90;

      setActiveFieldDirection(angleDeg);
    } else {
      setActiveFieldDirection(null);
    }
  }, [activeFieldId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return () => {};

    // Check immediately and after a small delay (for layout)
    checkFieldVisibility();
    const timeoutId = setTimeout(checkFieldVisibility, 100);

    // Listen for scroll events
    container.addEventListener('scroll', checkFieldVisibility, { passive: true });

    // Also listen for resize
    const resizeObserver = new ResizeObserver(checkFieldVisibility);
    resizeObserver.observe(container);

    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener('scroll', checkFieldVisibility);
      resizeObserver.disconnect();
    };
  }, [activeFieldId, checkFieldVisibility, loaded, dimensions]);

  const renderField = (field, scale, pageHeight) => {
    const fieldId = getFieldId(field);
    const isFilled = !!widgets[fieldId];
    const isActive = activeFieldId === fieldId;
    const value = widgets[fieldId];

    // Convert PDF coordinates to display coordinates
    const displayX = field.x * scale;
    const displayY = field.y * scale;

    // Use field dimensions if provided and reasonable, otherwise use sensible defaults
    const displayWidth = field.width && field.width > 0 ? field.width * scale : 150 * scale;
    const displayHeight =
      field.height && field.height > 0
        ? field.height * scale
        : (field.type === 'signature' ? 60 : 40) * scale;
    const browserY = pageHeight - displayY - displayHeight;

    const fieldStyle = {
      left: `${displayX}px`,
      top: `${browserY}px`,
      width: displayWidth,
      height: displayHeight,
    };

    const fieldProps = {
      isActive,
      isFilled,
      isMandatory: Boolean(field.mandatory || field.required),
      isSignature: field.type === 'signature',
      style: fieldStyle,
      onClick: () => handleFieldClick(field),
      ref: el => {
        fieldRefs.current[fieldId] = el;
      },
    };

    switch (field.type) {
      case 'signature':
        return (
          <FieldOverlay key={fieldId} {...fieldProps}>
            {isFilled ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={value.data}
                  alt="Signature"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            ) : (
              <Typography sx={fieldLabelSx}>{I18n.t('OmmnioSign.Fields.Signature')}</Typography>
            )}
          </FieldOverlay>
        );

      case 'text':
        return (
          <FieldOverlay
            key={fieldId}
            {...fieldProps}
            style={{ ...fieldStyle, padding: 0 }}
            onClick={isActive ? undefined : fieldProps.onClick}
          >
            {isActive ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1.5,
                }}
                onClick={e => e.stopPropagation()}
              >
                <InputBase
                  value={value || ''}
                  onChange={e => onWidgetChange(fieldId, e.target.value)}
                  onFocus={() => setActiveFieldId(fieldId)}
                  onBlur={() => handleFieldBlur(fieldId)}
                  placeholder={field.name || field.label || I18n.t('OmmnioSign.Fields.Text')}
                  sx={textInputSx}
                  autoFocus
                  fullWidth
                />
              </Box>
            ) : isFilled ? (
              <Box
                sx={{
                  padding: { xs: 0, sm: 1.5 },
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography sx={fieldLabelSx}>{value}</Typography>
                <CheckIcon sx={checkIconSx} />
              </Box>
            ) : (
              <Box sx={{ padding: { xs: 0, sm: 1.5 } }}>
                <Typography sx={fieldLabelSx}>
                  {field.name || field.label || I18n.t('OmmnioSign.Fields.Text')}
                </Typography>
              </Box>
            )}
          </FieldOverlay>
        );

      case 'date': {
        const displayDate = value ? normalizeOmmnioSignDate(value) : '';
        return (
          <FieldOverlay
            key={fieldId}
            {...fieldProps}
            style={{ ...fieldStyle, padding: 0 }}
            onClick={isActive ? undefined : fieldProps.onClick}
          >
            {isActive ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 0.5,
                }}
                onClick={e => e.stopPropagation()}
              >
                <InputBase
                  value={displayDate}
                  onChange={e => onWidgetChange(fieldId, e.target.value)}
                  onFocus={() => {
                    setActiveFieldId(fieldId);
                    if (!value) {
                      onWidgetChange(fieldId, formatOmmnioSignDate(new Date()));
                    }
                  }}
                  onBlur={e => {
                    const normalized = normalizeOmmnioSignDate(e.target.value);
                    if (normalized) {
                      onWidgetChange(fieldId, normalized);
                    }
                    handleFieldBlur(fieldId);
                  }}
                  placeholder="DD-MM-AAAA"
                  sx={textInputSx}
                  fullWidth
                  autoFocus
                  inputProps={{ inputMode: 'numeric' }}
                />
              </Box>
            ) : isFilled ? (
              <Box
                sx={{
                  padding: { xs: 0, sm: 1.5 },
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography sx={fieldLabelSx}>{displayDate}</Typography>
                <CheckIcon sx={checkIconSx} />
              </Box>
            ) : (
              <Box sx={{ padding: { xs: 0, sm: 1.5 } }}>
                <Typography sx={fieldLabelSx}>
                  {field.name || field.label || I18n.t('OmmnioSign.Fields.Date')}
                </Typography>
              </Box>
            )}
          </FieldOverlay>
        );
      }

      case 'checkbox':
        return (
          <FieldOverlay
            key={fieldId}
            {...fieldProps}
            style={{
              ...fieldStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
            onClick={() => handleFieldClick(field)}
          >
            <Checkbox
              checked={!!value}
              onChange={e => {
                e.stopPropagation();
                onWidgetChange(fieldId, e.target.checked);
                // Move to next unfilled field after checking
                const currentIndex = filteredFields.findIndex(f => getFieldId(f) === fieldId);
                const nextUnfilled = filteredFields
                  .slice(currentIndex + 1)
                  .find(nextField => !widgets[getFieldId(nextField)]);
                if (nextUnfilled) {
                  setActiveFieldId(getFieldId(nextUnfilled));
                } else {
                  setActiveFieldId(null);
                }
              }}
              sx={{
                color: isFilled ? '#28a745' : '#5568E8',
                '&.Mui-checked': { color: '#28a745' },
                padding: '0 !important',
                margin: 0,
              }}
            />
          </FieldOverlay>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={compact ? rootCompactSx : rootSx}
      className={compact ? 'ommniosign-pdf-root' : undefined}
    >
      <Box
        sx={compact ? previewWrapperCompactSx : previewWrapperSx}
        className={compact ? 'ommniosign-pdf-preview' : undefined}
      >
        <Box
          sx={scrollSx}
          className={compact ? 'ommniosign-pdf-scroll' : undefined}
          ref={containerRef}
        >
          <Suspense fallback={<Loading sx={{ mt: '20px' }} size={60} />}>
            {data && (
              <Document
                file={data}
                className={PDF_SCALE_CLASS}
                onLoadSuccess={pdf => {
                  setTotalPages(pdf.numPages);
                  setLoaded(true);
                }}
                loading={<Loading sx={{ mt: '20px' }} size={60} />}
                style={pdfSx}
              >
                {loaded &&
                  Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                    <Suspense
                      key={`page_${pageNum}`}
                      fallback={<Loading sx={{ mt: '20px' }} size={60} />}
                    >
                      <Page
                        canvasRef={null}
                        width={
                          containerRef.current?.offsetWidth
                            ? (containerRef.current.offsetWidth - 40) * zoom
                            : undefined
                        }
                        pageNumber={pageNum}
                        onLoadSuccess={page => {
                          // Store dimensions from the first page (most PDFs have consistent page sizes)
                          if (pageNum === 1 || !dimensions) {
                            setDimensions({
                              width: page.width,
                              height: page.height,
                              originalWidth: page.originalWidth,
                              originalHeight: page.originalHeight,
                            });
                          }
                        }}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        style={pageSx}
                      >
                        {dimensions && (
                          <Box sx={fieldsContainerSx}>
                            {filteredFields
                              ?.filter(field => (field.page || 1) === pageNum)
                              .map(field => {
                                const scale = dimensions.width / dimensions.originalWidth;
                                return renderField(field, scale, dimensions.height);
                              })}
                          </Box>
                        )}
                      </Page>
                    </Suspense>
                  ))}
              </Document>
            )}
          </Suspense>
          {compact ? <div className="ommniosign-pdf-scroll-end" aria-hidden /> : null}
        </Box>
      </Box>

      {/* Scroll indicator for off-screen active field */}
      {activeFieldDirection !== null && activeFieldId && (
        <ScrollIndicator
          className={compact ? 'ommniosign-pdf-scroll-indicator' : undefined}
          onClick={e => {
            e.stopPropagation();
            handleScrollToField(activeFieldId);
          }}
        >
          <Typography
            sx={{
              color: 'primary.contrastText',
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
          >
            {I18n.t('OmmnioSign.ScrollToNextField')}
          </Typography>
          <Box sx={{ display: 'flex' }} style={{ transform: `rotate(${activeFieldDirection}deg)` }}>
            <ScrollIndicatorIcon />
          </Box>
        </ScrollIndicator>
      )}

      {/* Zoom Controls */}
      <Box sx={zoomControlsSx} className={compact ? 'ommniosign-pdf-zoom-controls' : undefined}>
        <IconButton
          sx={zoomButtonSx}
          onClick={handleZoomIn}
          disabled={zoom >= MAX_ZOOM}
          size="small"
          aria-label="Zoom in"
        >
          <AddIcon />
        </IconButton>
        <Typography sx={zoomLabelSx} onClick={() => setZoom(1)} title="Reset to 100%">
          {Math.round(zoom * 100)}%
        </Typography>
        <IconButton
          sx={zoomButtonSx}
          onClick={handleZoomOut}
          disabled={zoom <= MIN_ZOOM}
          size="small"
          aria-label="Zoom out"
        >
          <RemoveIcon />
        </IconButton>
      </Box>

      {/* Signature Dialog */}
      <SignatureDialog
        open={signatureDialogOpen}
        fieldId={currentSignatureFieldIdRef.current}
        mandatory={
          currentSignatureFieldRef.current?.mandatory ??
          currentSignatureFieldRef.current?.required ??
          true
        }
        onClose={() => setSignatureDialogOpen(false)}
        onSave={handleSignatureSave}
      />
    </Box>
  );
};

PDFViewerWithOverlays.propTypes = {
  pdfData: object.isRequired,
  fields: arrayOf(
    shape({
      id: string.isRequired,
      type: string.isRequired,
      x: number.isRequired,
      y: number.isRequired,
      width: number,
      height: number,
      label: string,
      required: bool,
      page: number,
      signerIndex: number,
    }),
  ),
  widgets: object.isRequired,
  onWidgetChange: func.isRequired,
  onAllFieldsFilled: func,
  onScrollToBottom: func, // eIDAS compliance tracking
  currentSignerIndex: number, // For oneForAll mode - filter fields by signer
  compact: bool, // RN WebView: size to PDF content instead of filling viewport
};

export default PDFViewerWithOverlays;
